import {DEFAULT_SPRITE_IMAGE_URL} from '../constants/defaultAssets'

// Shared upload result fields. For anonymous users imageKey is null and imageUrl is a data URL.
export interface SpriteImage {
  imageKey: string | null
  imageUrl: string
  /** Natural width / natural height, captured at upload */
  imageAspect: number
}

export interface ScreenSprite extends SpriteImage {
  id: string
  /** Normalized 0..1 horizontal position (0 = left) */
  x: number
  /** Normalized 0..1 vertical position (0 = top) */
  y: number
  /** Rotation in degrees (Z axis only) */
  rotation: number
  /** Uniform scale multiplier (1 = reference size at the 1290-base frame) */
  scale: number
}

export interface Sprite3DMaterial {
  metalness: number
  roughness: number
  clearcoat: number
  clearcoatRoughness: number
  reflectivity: number
}

export interface SceneSprite3D extends SpriteImage {
  id: string
  /** World-space X position */
  x: number
  y: number
  z: number
  /** Rotation around X axis in degrees */
  rotationX: number
  rotationY: number
  rotationZ: number
  /** Plane's longest side in world units */
  scale: number
  /** When true the plane renders unlit (MeshBasicMaterial) AND drops out of the
   *  shadow render entirely (no cast, no receive). When false the plane uses a
   *  full PBR material and participates in shadows. */
  unlit: boolean
  /** PBR material properties — used only when unlit is false */
  material: Sprite3DMaterial
}

// IDs use `crypto.randomUUID()` rather than a module-level counter — a counter
// resets to 1 on every page load (and on every HMR module reload), so a project
// saved with `sprite3d-1` / `sprite3d-2` would collide as soon as the user added
// a third sprite (`sprite3d-1` again). UUIDs side-step that entirely without
// touching existing saved IDs (old `sprite3d-N` strings remain valid keys).
export const generateSprite2dId = (): string => `sprite2d-${crypto.randomUUID()}`
export const generateSprite3dId = (): string => `sprite3d-${crypto.randomUUID()}`

export const DEFAULT_3D_SPRITE_MATERIAL: Sprite3DMaterial = {
  metalness: 0.0,
  roughness: 0.5,
  clearcoat: 0.0,
  clearcoatRoughness: 0.1,
  reflectivity: 0.5,
}

/** Create a new 2D screen sprite. Defaults to the Ecranify gradient logo until the user uploads their own image. */
export const createScreenSprite = (): ScreenSprite => ({
  id: generateSprite2dId(),
  imageKey: null,
  imageUrl: DEFAULT_SPRITE_IMAGE_URL,
  imageAspect: 1,
  x: 0.5,
  y: 0.5,
  rotation: 0,
  scale: 1,
})

/** Create a new 3D world-space sprite. Defaults to the Ecranify gradient logo until the user uploads their own image. */
export const createSceneSprite3D = (): SceneSprite3D => ({
  id: generateSprite3dId(),
  imageKey: null,
  imageUrl: DEFAULT_SPRITE_IMAGE_URL,
  imageAspect: 1,
  x: 0,
  y: 90,
  z: 0,
  rotationX: 0,
  rotationY: 0,
  rotationZ: 0,
  scale: 40,
  unlit: false,
  material: {...DEFAULT_3D_SPRITE_MATERIAL},
})
